import { InputRef, notification } from "antd";
import { useRef, useState, useMemo, useCallback } from "react";
import { useTranslate, useUpdate } from "@refinedev/core";
import { MediaProject, ProjectTag } from "../../types";
import { ReservedProjectTags } from "../types";
import {
  filterOutReservedTags,
  getFullTagList,
  sortTagsAlphabetically,
} from "../helpers/helper";
import { isReservedProjectTag } from "../helpers/project-create-wizard";

export const useProjectTags = (
  projectId: string | undefined,
  initialTags: ProjectTag[] = []
) => {
  const t = useTranslate();
  const { mutateAsync: update, isPending: isUpdating } =
    useUpdate<MediaProject>();

  const filteredInitialTags = useMemo(
    () =>
      initialTags.filter(
        (tag) => tag.tag_key !== ReservedProjectTags.WEB_PAGE_URL
      ),
    [initialTags]
  );

  const editInputRef = useRef<InputRef>(null);
  const addInputRef = useRef<InputRef>(null);
  const [tagValues, setTagValues] = useState<ProjectTag[]>(filteredInitialTags);
  const [editInputIndex, setEditInputIndex] = useState<number>(-1);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const validateTagDuplication = useCallback(
    (inputValue: string, excludeIndex?: number) => {
      if (!inputValue) return false;

      const isExistingTag = tagValues.some(
        (tag, index) => tag.tag_key === inputValue && index !== excludeIndex
      );

      if (isExistingTag) {
        notification.warning({
          message: t(
            "media.components.ProjectVideo.ProjectTagList.tagAlreadyExists"
          ),
        });

        if (excludeIndex >= 0) {
          // Restore original value for edit input
          const originalTag = tagValues[excludeIndex];
          if (originalTag && editInputRef.current?.input) {
            editInputRef.current.input.value = originalTag.tag_value;
          }
          setEditInputIndex(-1);
        } else if (addInputRef.current?.input) {
          addInputRef.current.input.value = "";
        }
        return false;
      }
      return true;
    },
    [tagValues, t]
  );

  const handleSave = useCallback(
    async (values: { tags: ProjectTag[] }) => {
      try {
        await update({
          resource: "media/projects",
          id: projectId,
          values,
          successNotification() {
            return false;
          },
        });
        return true;
      } catch (error) {
        console.error("Error updating project tags", error);
        return false;
      }
    },
    [projectId, update]
  );

  const handleEditConfirm = useCallback(() => {
    const inputValue = editInputRef.current?.input?.value?.trim() || "";
    if (!validateTagDuplication(inputValue, editInputIndex)) return;

    setIsSaving(true);
    const newTagValues = [...tagValues];
    const currentTag = newTagValues[editInputIndex];
    if (currentTag) {
      newTagValues[editInputIndex] = {
        ...currentTag,
        tag_value: inputValue,
      };
      // Auto-save after editing
      handleSave({
        tags: getFullTagList(
          initialTags,
          filterOutReservedTags(newTagValues).map((tag) => tag.tag_value)
        ),
      })
        .then((success) => {
          if (success) {
            setTagValues(newTagValues);
            setEditInputIndex(-1);
            if (editInputRef.current?.input) {
              editInputRef.current.input.value = "";
            }
          }
        })
        .catch(console.error)
        .finally(() => setIsSaving(false));
    }
  }, [
    editInputIndex,
    tagValues,
    validateTagDuplication,
    handleSave,
    initialTags,
  ]);

  const handleAddTag = useCallback(() => {
    // If editing is in progress, restore the original edit value
    if (editInputIndex >= 0) {
      const originalTag = tagValues[editInputIndex];
      const currentEditValue = editInputRef.current?.input?.value || "";
      if (originalTag && currentEditValue !== originalTag.tag_value) {
        if (editInputRef.current?.input) {
          editInputRef.current.input.value = originalTag.tag_value;
        }
        setEditInputIndex(-1);
      }
    }

    const inputValue = addInputRef.current?.input?.value?.trim() || "";
    if (!validateTagDuplication(inputValue)) return;
    if (inputValue) {
      setIsSaving(true);

      const newTagValues = [
        ...tagValues,
        { tag_key: inputValue, tag_value: inputValue },
      ];

      // Auto-save after adding
      handleSave({
        tags: getFullTagList(
          initialTags,
          filterOutReservedTags(newTagValues).map((tag) => tag.tag_value)
        ),
      })
        .then((success) => {
          if (success) {
            setTagValues(newTagValues);

            if (addInputRef.current?.input) {
              addInputRef.current.input.value = "";
            }
            setTimeout(() => addInputRef.current?.focus(), 50);
          }
        })
        .catch(console.error)
        .finally(() => setIsSaving(false));
    }
  }, [
    editInputIndex,
    tagValues,
    validateTagDuplication,
    handleSave,
    initialTags,
  ]);

  const handleTagDoubleClick = useCallback((tag: ProjectTag, index: number) => {
    if (isReservedProjectTag(tag.tag_key)) return;

    // Clear add input if it has unsaved value
    const addInputValue = addInputRef.current?.input?.value?.trim() || "";
    if (addInputValue && addInputRef.current?.input) {
      addInputRef.current.input.value = "";
    }

    if (editInputRef.current?.input) {
      editInputRef.current.input.value = tag.tag_value;
    }
    setEditInputIndex(index);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }, []);

  const handleTagClose = useCallback(
    (index: number) => {
      const newTagValues = [...tagValues];
      newTagValues.splice(index, 1);

      // Auto-save after deleting
      handleSave({
        tags: getFullTagList(
          initialTags,
          filterOutReservedTags(newTagValues).map((tag) => tag.tag_value)
        ),
      })
        .then((success) => {
          if (success) {
            setTagValues(newTagValues);
          }
        })
        .catch(console.error)
        .finally(() => setIsSaving(false));
    },
    [tagValues, handleSave, initialTags]
  );

  const resetTags = useCallback(() => {
    setTagValues(filteredInitialTags);
    if (editInputRef.current?.input) {
      editInputRef.current.input.value = "";
    }
    if (addInputRef.current?.input) {
      addInputRef.current.input.value = "";
    }
    setEditInputIndex(-1);
  }, [filteredInitialTags]);

  const hasUnsavedChanges = useMemo(() => {
    if (tagValues.length !== filteredInitialTags.length) {
      return true;
    }

    const originalTagValues = sortTagsAlphabetically(filteredInitialTags);
    const currentTagValues = sortTagsAlphabetically(tagValues);

    return (
      JSON.stringify(originalTagValues) !== JSON.stringify(currentTagValues)
    );
  }, [tagValues, filteredInitialTags]);

  return {
    tagValues,
    editInputIndex,
    setEditInputIndex,
    editInputRef,
    addInputRef,
    isUpdating,
    isSaving,
    hasUnsavedChanges,
    handlers: {
      onAddNewTag: handleAddTag,
      onEditConfirm: handleEditConfirm,
      onTagDoubleClick: handleTagDoubleClick,
      onTagClose: handleTagClose,
      onReset: resetTags,
    },
  };
};
